import { isCross, isDelta1Multileg, ProductTypeEnum, SyntheticProductTypeEnum } from '@talos/kyoko';
import { defaultPriceGridSourceFilter } from '../../hooks';
import type { PriceGridFilterConfig, PriceGridFilterState, PriceGridSourceItem } from '../../types';

const DEFAULT_MULTILEG_PRODUCT_TYPES = [
  ProductTypeEnum.Synthetic,
  ProductTypeEnum.Basis,
  ProductTypeEnum.CFD,
  ProductTypeEnum.CalendarSpread,
] as const;

export const MultilegPriceGridType = {
  Cross: 'Cross',
  Delta1: 'Delta1',
  CalendarSpread: 'CalendarSpread',
  VerticalSpread: 'VerticalSpread',
} as const;
export type MultilegPriceGridType = (typeof MultilegPriceGridType)[keyof typeof MultilegPriceGridType];

export const MULTILEG_PRICE_GRID_TYPE_OPTIONS = [
  { type: MultilegPriceGridType.Cross, label: 'Cross' },
  { type: MultilegPriceGridType.Delta1, label: 'Delta-1' },
  { type: MultilegPriceGridType.CalendarSpread, label: 'Cal Spread' },
  { type: MultilegPriceGridType.VerticalSpread, label: 'Ver Spread' },
] as const;

export function getSelectedMultilegTypes(filterState: PriceGridFilterState) {
  return (filterState.custom?.multilegTypes as MultilegPriceGridType[] | undefined) ?? [];
}

function matchesMultilegType(sourceItem: PriceGridSourceItem, selectedTypes: MultilegPriceGridType[]) {
  if (selectedTypes.length === 0) {
    return true;
  }

  const security = sourceItem.security;

  return selectedTypes.some(type => {
    switch (type) {
      case MultilegPriceGridType.Cross:
        return isCross(security);
      case MultilegPriceGridType.Delta1:
        return isDelta1Multileg(security);
      case MultilegPriceGridType.CalendarSpread:
        return isCalendarSpreadMultileg(sourceItem);
      case MultilegPriceGridType.VerticalSpread:
        return isVerticalSpreadMultileg(sourceItem);
      default:
        return false;
    }
  });
}

function isMultilegPriceGridSecurity(sourceItem: PriceGridSourceItem) {
  return (sourceItem.security.MultilegDetails?.Legs?.length ?? 0) >= 2;
}

function isCalendarSpreadMultileg(sourceItem: PriceGridSourceItem) {
  const syntheticProductType = sourceItem.security.MultilegDetails?.SyntheticProductType;
  return (
    sourceItem.security.ProductType === ProductTypeEnum.CalendarSpread ||
    syntheticProductType === SyntheticProductTypeEnum.CallCalendarSpread ||
    syntheticProductType === SyntheticProductTypeEnum.PutCalendarSpread
  );
}

function isVerticalSpreadMultileg(sourceItem: PriceGridSourceItem) {
  const syntheticProductType = sourceItem.security.MultilegDetails?.SyntheticProductType;
  return (
    syntheticProductType === SyntheticProductTypeEnum.CallSpread ||
    syntheticProductType === SyntheticProductTypeEnum.PutSpread
  );
}

export const MULTILEG_PRICE_GRID_FILTER_CONFIG = {
  initialState: {
    productTypes: [...DEFAULT_MULTILEG_PRODUCT_TYPES],
  },
  clientFilter: (sourceItem, filterState) =>
    isMultilegPriceGridSecurity(sourceItem) &&
    defaultPriceGridSourceFilter(sourceItem, filterState) &&
    matchesMultilegType(sourceItem, getSelectedMultilegTypes(filterState)),
} satisfies PriceGridFilterConfig<PriceGridSourceItem>;
